import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Tooltip,
  Collapse,
  Grid,
  Divider,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Autorenew as RegenerateIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  PhoneAndroid as DeviceIcon,
  Settings as ConfigIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { telephonyApi, devicesApi } from '../../services/apiClient';
import { getDeviceLanIP } from '../../utils/deviceHelpers';
import ProvisionSipDeviceDialog from './ProvisionSipDeviceDialog';

// Default Asterisk connection settings shown in the config guide
const ASTERISK_DEFAULTS = {
  server: 'phone.caro.care',
  tlsPort: 5061,
  udpPort: 5060,
  transport: 'TLS',
  realm: 'caro.care',
  codec: 'G.711 μ-law (PCMU)',
};

const TRANSPORT_OPTIONS = [
  { value: 'tls', label: 'TLS (Recommended)' },
  { value: 'udp', label: 'UDP (Fallback)' },
];

const CONTEXT_OPTIONS = [
  { value: 'caro-videophone', label: 'Videophone / Entrance Panel' },
  { value: 'caro-internal', label: 'Internal (Resident/Common Area)' },
  { value: 'caro-staff', label: 'Staff' },
  { value: 'caro-emergency', label: 'Emergency' },
];

export default function SipEndpointsTab({ t }) {
  const [endpoints, setEndpoints] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [orderBy, setOrderBy] = useState('extension');
  const [order, setOrder] = useState('asc');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [dialogData, setDialogData] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Provision dialog
  const [provisionDialogOpen, setProvisionDialogOpen] = useState(false);

  // Devices list for linking
  const [devices, setDevices] = useState([]);

  const fetchEndpoints = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (search) params.search = search;

      const { data } = await telephonyApi.getSipEndpoints(params);
      const list = data.endpoints || data.data || data || [];
      setEndpoints(Array.isArray(list) ? list : []);
      setTotal(Array.isArray(list) ? list.length : 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load SIP endpoints');
      setEndpoints([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await devicesApi.getAll();
      const list = data.devices || data.data || data || [];
      setDevices(Array.isArray(list) ? list : []);
    } catch {
      setDevices([]);
    }
  }, []);

  useEffect(() => { fetchEndpoints(); }, [fetchEndpoints]);
  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const sortedEndpoints = useMemo(() => {
    if (!orderBy) return endpoints;
    return [...endpoints].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      if (orderBy === 'extension') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [endpoints, orderBy, order]);

  // ─── Create / Edit ──────────────────────────────────────────
  const openCreateDialog = () => {
    setDialogMode('create');
    setDialogData({
      extension: '',
      displayName: '',
      password: '',
      transport: 'tls',
      context: 'caro-videophone',
      deviceId: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  const openEditDialog = (endpoint) => {
    setDialogMode('edit');
    setDialogData({
      id: endpoint.id,
      extension: endpoint.extension,
      displayName: endpoint.displayName || endpoint.display_name || '',
      password: endpoint.password || '',
      transport: endpoint.transport || 'tls',
      context: endpoint.context || 'caro-videophone',
      deviceId: endpoint.deviceId || endpoint.device_id || '',
      notes: endpoint.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!dialogData.displayName) return;

    try {
      setSaving(true);
      const payload = {
        extension: String(dialogData.extension),
        display_name: dialogData.displayName,
        password: dialogData.password || undefined,
        transport: dialogData.transport,
        context: dialogData.context,
        device_id: dialogData.deviceId || null,
        notes: dialogData.notes || null,
      };

      if (dialogMode === 'create') {
        await telephonyApi.createSipEndpoint(payload);
        setSnackbar({ open: true, message: 'SIP endpoint created', severity: 'success' });
      } else {
        await telephonyApi.updateSipEndpoint(dialogData.id, payload);
        setSnackbar({ open: true, message: 'SIP endpoint updated', severity: 'success' });
      }

      setDialogOpen(false);
      fetchEndpoints();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.message || 'Failed to save',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      setDeleting(true);
      await telephonyApi.deleteSipEndpoint(deleteDialog.id);
      setSnackbar({ open: true, message: 'SIP endpoint deleted', severity: 'success' });
      setDeleteDialog(null);
      fetchEndpoints();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to delete',
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  // ─── Regenerate credentials ─────────────────────────────────
  const handleRegenerate = async (endpoint) => {
    try {
      const { data } = await telephonyApi.regenerateSipCredentials(endpoint.id);
      setSnackbar({ open: true, message: 'Credentials regenerated', severity: 'success' });
      fetchEndpoints();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to regenerate credentials',
        severity: 'error',
      });
    }
  };

  // ─── Reload Asterisk ────────────────────────────────────────
  const handleReloadAsterisk = async () => {
    try {
      await telephonyApi.reloadAsterisk();
      setSnackbar({ open: true, message: 'Asterisk configuration reloaded', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to reload Asterisk',
        severity: 'error',
      });
    }
  };

  const getStatusChip = (endpoint) => {
    if (endpoint.registered || endpoint.is_registered) {
      return <Chip label="Registered" size="small" icon={<OnlineIcon fontSize="small" />} sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, fontSize: '0.75rem' }} />;
    }
    return <Chip label="Not Registered" size="small" icon={<OfflineIcon fontSize="small" />} sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600, fontSize: '0.75rem' }} />;
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search extension, name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchEndpoints(); }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
          sx={{ minWidth: 260 }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add SIP Endpoint
        </Button>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          startIcon={<PhoneIcon />}
          onClick={() => setProvisionDialogOpen(true)}
        >
          Provision Location
        </Button>
        <Tooltip title="Reload Asterisk PJSIP config">
          <Button
            size="small"
            variant="outlined"
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={handleReloadAsterisk}
          >
            Reload Asterisk
          </Button>
        </Tooltip>
        <IconButton onClick={fetchEndpoints} size="small">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Info banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Configure SIP endpoints for physical devices (2N intercoms, Grandstream phones, door stations, etc.) that register with Asterisk via TLS or UDP.
        Expand a row to see the exact settings to configure on the device.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sortDirection={orderBy === 'extension' ? order : false}>
                <TableSortLabel active={orderBy === 'extension'} direction={orderBy === 'extension' ? order : 'asc'} onClick={() => handleSort('extension')}>
                  Extension
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'displayName' ? order : false}>
                <TableSortLabel active={orderBy === 'displayName'} direction={orderBy === 'displayName' ? order : 'asc'} onClick={() => handleSort('displayName')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Transport</TableCell>
              <TableCell>Context</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Linked Device</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : sortedEndpoints.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No SIP endpoints configured. Click "Add SIP Endpoint" to create one.
                </TableCell>
              </TableRow>
            ) : sortedEndpoints.map((ep) => {
              const isExpanded = expandedRow === ep.id;
              return (
                <React.Fragment key={ep.id}>
                  <TableRow
                    hover
                    onClick={() => setExpandedRow(isExpanded ? null : ep.id)}
                    sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'none' : undefined } }}
                  >
                    <TableCell sx={{ width: 40, px: 1 }}>
                      <IconButton size="small">
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem' }}>
                      {ep.extension}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {ep.displayName || ep.display_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(ep.transport || 'tls').toUpperCase()}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          bgcolor: ep.transport === 'udp' ? '#FFF3E0' : '#E3F2FD',
                          color: ep.transport === 'udp' ? '#E65100' : '#1565C0',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {ep.context || 'caro-videophone'}
                      </Typography>
                    </TableCell>
                    <TableCell>{getStatusChip(ep)}</TableCell>
                    <TableCell>
                      {ep.deviceName || ep.device_name ? (
                        <Chip
                          icon={<DeviceIcon fontSize="small" />}
                          label={ep.deviceName || ep.device_name}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(ep)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Regenerate password">
                        <IconButton size="small" onClick={() => handleRegenerate(ep)} color="warning">
                          <RegenerateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteDialog(ep)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>

                  {/* Expandable config guide */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, px: 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <DeviceConfigGuide endpoint={ep} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <EndpointDialog
        open={dialogOpen}
        mode={dialogMode}
        data={dialogData}
        devices={devices}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onChange={setDialogData}
        onSave={handleSave}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs">
        <DialogTitle>Delete SIP Endpoint?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove extension <strong>{deleteDialog?.extension}</strong> ({deleteDialog?.displayName || deleteDialog?.display_name}).
            The device will no longer be able to register with Asterisk.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Provision Location Dialog */}
      <ProvisionSipDeviceDialog
        open={provisionDialogOpen}
        onClose={() => setProvisionDialogOpen(false)}
        location={null}
        onSuccess={fetchEndpoints}
      />
    </>
  );
}

// ─── Device Configuration Guide (expandable row) ──────────────
function DeviceConfigGuide({ endpoint }) {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState('');

  const transport = (endpoint.transport || 'tls').toLowerCase();
  const port = transport === 'tls' ? ASTERISK_DEFAULTS.tlsPort : ASTERISK_DEFAULTS.udpPort;

  const configFields = [
    { label: 'SIP Registrar / Server', value: ASTERISK_DEFAULTS.server, key: 'server' },
    { label: 'Port', value: String(port), key: 'port' },
    { label: 'Transport', value: transport.toUpperCase(), key: 'transport' },
    { label: 'SIP Username / Auth ID', value: endpoint.extension, key: 'username' },
    { label: 'Password / Secret', value: endpoint.password || '••••••••', key: 'password', sensitive: true },
    { label: 'Realm / Domain', value: ASTERISK_DEFAULTS.realm, key: 'realm' },
    { label: 'Caller ID', value: `"${endpoint.displayName || endpoint.display_name}" <${endpoint.extension}>`, key: 'callerid' },
    { label: 'Audio Codec', value: ASTERISK_DEFAULTS.codec, key: 'codec' },
  ];

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    });
  };

  return (
    <Box sx={{ bgcolor: 'action.hover', px: 3, py: 2 }}>
      <Grid container spacing={3}>
        {/* Config Guide */}
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <ConfigIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
              Device SIP Configuration
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Enter these settings in the device's SIP / VoIP configuration panel.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {configFields.map((field) => (
              <Box
                key={field.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.5,
                  px: 1.5,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 180, fontSize: '0.82rem' }}>
                  {field.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    flex: 1,
                  }}
                >
                  {field.sensitive && !showPassword ? '••••••••' : field.value}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {field.sensitive && (
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                    </IconButton>
                  )}
                  <Tooltip title={copied === field.key ? 'Copied!' : 'Copy'}>
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(field.sensitive ? (endpoint.password || '') : field.value, field.key)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Registration Status & Notes */}
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Registration Info
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ConfigDetail label="Status" value={
              (endpoint.registered || endpoint.is_registered)
                ? <Chip label="Registered" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32', fontWeight: 600 }} />
                : <Chip label="Not Registered" size="small" sx={{ bgcolor: '#FFEBEE', color: '#C62828', fontWeight: 600 }} />
            } />
            {endpoint.registeredAt && (
              <ConfigDetail label="Last Registered" value={new Date(endpoint.registeredAt).toLocaleString()} />
            )}
            {endpoint.userAgent && (
              <ConfigDetail label="User Agent" value={endpoint.userAgent} mono />
            )}
            {endpoint.contactIp && (
              <ConfigDetail label="Contact IP" value={endpoint.contactIp} mono />
            )}
            {endpoint.notes && (
              <ConfigDetail label="Notes" value={endpoint.notes} />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
            Quick Setup Guide
          </Typography>
          <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" component="div" sx={{ lineHeight: 1.8 }}>
              1. Open the device web interface (admin panel)<br />
              2. Navigate to the SIP / VoIP settings section<br />
              3. Set <strong>SIP Proxy / Registrar</strong> to <code>{ASTERISK_DEFAULTS.server}:{port}</code><br />
              4. Set <strong>Username / Phone Number</strong> to <code>{endpoint.extension}</code><br />
              5. Set <strong>Auth ID</strong> to <code>{endpoint.extension}</code><br />
              6. Set <strong>Password</strong> to the password shown above<br />
              7. Set <strong>Transport</strong> to <strong>{transport.toUpperCase()}</strong><br />
              8. Set <strong>Audio Codec</strong> to <strong>G.711 μ-law (PCMU)</strong><br />
              9. Apply settings and verify registration status
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

function ConfigDetail({ label, value, mono }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 120, fontSize: '0.82rem' }}>
        {label}:
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: mono ? 'monospace' : 'inherit', fontSize: mono ? '0.8rem' : undefined }}>
          {value}
        </Typography>
      ) : value}
    </Box>
  );
}

// ─── Create/Edit Dialog ─────────────────────────────────────────
function EndpointDialog({ open, mode, data, devices, saving, onClose, onChange, onSave }) {
  if (!data) return null;

  // Build device options with searchable labels
  const deviceOptions = useMemo(() => {
    return devices.map((d) => {
      const name = d.friendlyName || d.name || `Device #${d.id}`;
      const ip = getDeviceLanIP(d) || '';
      const typeName = d.deviceTypeModel?.friendlyName || d.deviceTypeName || d.device_type_name || '';
      return {
        id: d.id,
        name,
        ip,
        typeName,
      };
    });
  }, [devices]);

  const selectedDevice = deviceOptions.find((d) => d.id === data.deviceId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Add SIP Endpoint' : `Edit SIP Endpoint — ${data.extension}`}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Context / Role</InputLabel>
            <Select
              value={data.context}
              label="Context / Role"
              onChange={(e) => onChange({ ...data, context: e.target.value })}
            >
              {CONTEXT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Extension"
              value={data.extension}
              onChange={(e) => onChange({ ...data, extension: e.target.value })}
              size="small"
              sx={{ width: 160 }}
              required
              placeholder="e.g. 7001"
              helperText={mode === 'create' ? 'Leave empty to auto-assign' : undefined}
              disabled={mode === 'edit'}
            />
            <TextField
              label="Display Name"
              value={data.displayName}
              onChange={(e) => onChange({ ...data, displayName: e.target.value })}
              size="small"
              fullWidth
              required
              placeholder='e.g. "Front Door", "Garage Entrance"'
            />
          </Box>

          <TextField
            label={mode === 'create' ? 'Password (leave blank to auto-generate)' : 'Password'}
            value={data.password}
            onChange={(e) => onChange({ ...data, password: e.target.value })}
            size="small"
            fullWidth
            placeholder={mode === 'create' ? 'Auto-generated if empty' : ''}
            helperText="SIP authentication password for this device"
          />

          <FormControl fullWidth size="small">
            <InputLabel>Transport</InputLabel>
            <Select
              value={data.transport}
              label="Transport"
              onChange={(e) => onChange({ ...data, transport: e.target.value })}
            >
              {TRANSPORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={deviceOptions}
            value={selectedDevice}
            onChange={(_, newValue) => onChange({ ...data, deviceId: newValue?.id || '' })}
            getOptionLabel={(option) => option.name}
            filterOptions={(options, { inputValue }) => {
              const q = inputValue.toLowerCase();
              if (!q) return options;
              return options.filter((o) =>
                o.name.toLowerCase().includes(q) ||
                o.ip.toLowerCase().includes(q) ||
                o.typeName.toLowerCase().includes(q) ||
                String(o.id).includes(q)
              );
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[option.typeName, option.ip].filter(Boolean).join(' · ') || `ID: ${option.id}`}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Link to Device (Optional)"
                placeholder="Search by name, IP, or type..."
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            clearOnBlur
            clearOnEscape
          />

          <TextField
            label="Notes (Optional)"
            value={data.notes}
            onChange={(e) => onChange({ ...data, notes: e.target.value })}
            size="small"
            fullWidth
            multiline
            rows={2}
            placeholder="Location, model, serial number..."
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={saving || !data.displayName}
        >
          {saving ? <CircularProgress size={20} /> : mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
